import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const TimesheetContext = createContext();

// ---- JWT helpers (decode without verifying) ----
// We only inspect `exp` to drive the session-expiry banner & soft-redirect.
// Verification is the backend's job; here we just want the timestamp.
const decodeJwt = (jwt) => {
  try {
    const [, payload] = (jwt || '').split('.');
    if (!payload) return null;
    const base = payload.replace(/-/g, '+').replace(/_/g, '/');
    const json = atob(base + '='.repeat((4 - base.length % 4) % 4));
    return JSON.parse(json);
  } catch (e) { return null; }
};

const SESSION_WARN_BEFORE_MS = 5 * 60 * 1000; // 5 min
// Silent token refresh fires every 12 minutes while the tab is open.
// Aligned with AuthContext's shell interval so both windows tick at the
// same cadence, AND stays comfortably ahead of the 15-min access-token
// TTL Phase A cuts over to once the frontend migration is verified
// (docs/AUTH_REDESIGN_2026-07.md). During the grace period access tokens
// are still 24h, so 12 min is safely-aggressive; after the cutover, it's
// the whole session-keep-alive mechanism.
const REFRESH_INTERVAL_MS = 12 * 60 * 1000; // 12 min

export const useTimesheet = () => {
  const context = useContext(TimesheetContext);
  if (!context) throw new Error('useTimesheet must be inside TimesheetProvider');
  return context;
};

// One-shot cleanup of the legacy localStorage cache. Older builds persisted
// the JWT here; the new build keeps it in memory only. This wipe stops the
// stale token from lingering after users upgrade. Safe to remove once every
// active browser has run the new build at least once.
try { if (typeof window !== 'undefined') window.localStorage.removeItem('harmony_ts_token'); } catch (_) {}

export const TimesheetProvider = ({ children, token, role, userName }) => {
  const [loading, setLoading] = useState(false);
  // Active token lives in a ref so silent refresh can swap it without
  // re-creating any useCallback API wrappers. The request interceptor (added
  // below) reads tokenRef on EVERY outgoing request to BACKEND_URL, so the
  // moment the ref updates, the next API call uses the new token.
  // The local `headers` constant is kept for backward-compat with the
  // existing call sites that pass `{ headers }`; the interceptor overrides
  // its Authorization with the latest ref value, so a stale closure copy
  // can't pin requests to an old token.
  // Start with the URL-launched token. If it's already stale on a bookmark
  // reopen, the silent-refresh useEffect below fires immediately and swaps
  // it via the HttpOnly harmony_refresh cookie — no localStorage cache
  // needed. Keeping the JWT in memory only is the whole point (an XSS in
  // any component that reads localStorage would exfiltrate it otherwise).
  const tokenRef = useRef(token);
  useEffect(() => {
    // Prop token change (e.g. parent re-mounts with fresh URL token) —
    // only overwrite if the incoming URL token is actually fresher than
    // what we're holding, otherwise a stale relaunch would downgrade us.
    try {
      const incoming = decodeJwt(token);
      const current = decodeJwt(tokenRef.current);
      if ((incoming?.exp || 0) > (current?.exp || 0)) {
        tokenRef.current = token;
      }
    } catch (_) {
      tokenRef.current = token;
    }
  }, [token]);
  const headers = { Authorization: `Bearer ${token}` };
  const API = `${BACKEND_URL}/api/timesheet`;

  // System role from URL is the initial guess; authoritative role comes from /me/role below.
  const isSuperAdmin = role === 'super_admin';
  const [meRole, setMeRole] = useState({
    system_role: role,
    timesheet_role: 'member',
    effective_role: role === 'super_admin' || role === 'hr_admin' ? 'admin' : 'member',
    is_admin: role === 'super_admin' || role === 'hr_admin',
    is_reviewer: false,
    is_super_admin: role === 'super_admin',
  });

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/me/role`, { headers });
        if (alive) setMeRole(r.data);
      } catch (e) { /* fall back to URL-derived role */ }
    })();
    return () => { alive = false; };
  }, [token]);

  // ---- Session: silent refresh + 401 redirect (scoped to timesheet API) ----
  // The timesheet app runs in its own browser window launched by the harmony
  // dashboard. Three concerns handled here:
  //   1. Request interceptor injects the LATEST token from tokenRef on every
  //      backend call, so silent-refresh updates take effect on the next call
  //      without rewiring any useCallback API wrappers.
  //   2. A 20h-interval silent refresh swaps the token in-place so a
  //      continuously-active user never sees an unexpected logout.
  //   3. On 401 from /api/timesheet/* OR on hard expiry, redirect to the
  //      harmony shell login page (origin/login) so the user can re-auth and
  //      re-launch the app — replaces the old window.close() behaviour.
  useEffect(() => {
    if (!token) return;
    const timers = [];
    let cancelled = false;
    let redirecting = false;

    const goToShellLogin = (reason) => {
      if (redirecting) return; // dedupe: don't fire a second toast / queue a second redirect
      redirecting = true;
      const loginUrl = `${window.location.origin}/login`;
      // 4s gives the user time to read the toast; if they were mid-edit at
      // least they SEE the redirect coming. (Original 1.5s was too quick —
      // reviewer flagged this.)
      toast.error(`Session expired — redirecting to login in 4 seconds. ${reason || ''}`.trim(), { duration: 4000 });
      setTimeout(() => {
        if (!cancelled) { try { window.location.href = loginUrl; } catch (_) { /* noop */ } }
      }, 4000);
    };

    // 1. Request interceptor — only acts on TIMESHEET URLs (matches the
    // response interceptor's scope below). Other backend endpoints — and
    // any other app sharing the global axios instance — flow through with
    // whatever Authorization header their own code already set.
    const reqId = axios.interceptors.request.use((config) => {
      const url = config.url || '';
      // /api/runn/* is served from the same backend and consumed by the
      // Runn tabs living inside this sub-app, so route it through the
      // same auth interceptor. Same silent-refresh behaviour applies.
      const isSubAppCall = url.includes('/api/timesheet/')
        || url.includes('/api/runn/')
        || url === `${BACKEND_URL}/api/auth/refresh`;
      if (isSubAppCall) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${tokenRef.current}`;
      }
      return config;
    });

    // 2. Silent refresh — fire on REFRESH_INTERVAL_MS while the tab is
    // open. `withCredentials: true` is what delivers the HttpOnly
    // harmony_refresh cookie set by the shell at login — without it the
    // cookie never rides along, and the backend either has to fall back
    // to the legacy Authorization-header path OR return 401 outright.
    // The sub-app opens in its own window, so setting withCredentials on
    // axios.defaults from AuthContext doesn't reach here — hence the
    // per-call flag on refresh AND on the response interceptor's retry.
    // On 401 from refresh (deactivated user, revoked token, session
    // truly dead), redirect immediately rather than leaving the user
    // with a token they think works.
    const doRefresh = async () => {
      try {
        const r = await axios.post(
          `${BACKEND_URL}/api/auth/refresh`,
          {},
          { withCredentials: true },
        );
        if (r.data?.token && !cancelled) {
          tokenRef.current = r.data.token;
        }
      } catch (e) {
        if (e?.response?.status === 401) {
          goToShellLogin('Your session is no longer valid.');
        }
        // Else: silently continue; next protected call will surface any issue.
      }
    };
    timers.push(setInterval(doRefresh, REFRESH_INTERVAL_MS));

    // Bookmark-reopen case: the URL still holds the JWT the shell launched
    // us with, which may now be close to (or past) its exp. Fire one
    // proactive refresh at mount ONLY when it's within 5 min of expiry —
    // that's when the interceptor 401-recovery would otherwise kick in on
    // the very first protected call. No cache to fall back on; the refresh
    // cookie carries us. On a fresh launch the URL token is minutes-old
    // and this fast-path is skipped.
    (() => {
      const claimsAtMount = decodeJwt(tokenRef.current);
      const expAtMountMs = claimsAtMount?.exp ? claimsAtMount.exp * 1000 : 0;
      if (!expAtMountMs || expAtMountMs - Date.now() < SESSION_WARN_BEFORE_MS) {
        doRefresh();
      }
    })();

    // Note: an older revision of this file also scheduled a "session
    // expires in 5 minutes" warning toast and a hard-expiry redirect
    // relative to the URL-launched token's exp claim. Both fired
    // spuriously — the warning always tripped at ~t+10min because the
    // 12-minute silent refresh below hadn't run yet, and the hard-expiry
    // redirect was made redundant by the response interceptor's silent
    // 401-recovery. Real session death is now surfaced ONLY by:
    //   1. doRefresh() catching a 401 from /auth/refresh (session
    //      revoked / user deactivated), or
    //   2. the response interceptor exhausting one refresh-and-retry
    //      cycle and still seeing 401.
    // Both call goToShellLogin. Any other transient condition (network
    // flap, brief backend hiccup) resolves without user-facing noise.

    // 4. Response interceptor — scoped 401 handler for /api/timesheet/*.
    // Instead of redirecting on the first 401, attempt one silent refresh:
    // if it succeeds the user's session keeps working (they can retry the
    // failed action). Only if refresh itself fails do we redirect — that's
    // when we know the session is genuinely dead (token revoked, user
    // deactivated, etc.). The `_recoveryAttempted` flag on the failing
    // request prevents an infinite loop if the retried call also 401s.
    const respId = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const url = error?.config?.url || '';
        const cfg = error?.config || {};
        const isSubApp401 = error?.response?.status === 401
          && (url.includes('/api/timesheet/') || url.includes('/api/runn/'));
        if (!isSubApp401 || cfg._recoveryAttempted) {
          if (isSubApp401) goToShellLogin('Please log in again.');
          return Promise.reject(error);
        }
        try {
          // withCredentials so the harmony_refresh cookie rides along —
          // same rationale as the scheduled refresh above.
          const r = await axios.post(
            `${BACKEND_URL}/api/auth/refresh`,
            {},
            { withCredentials: true },
          );
          if (r.data?.token) {
            tokenRef.current = r.data.token;
            // Retry the original request once with the new token. The request
            // interceptor will pick up tokenRef.current automatically.
            cfg._recoveryAttempted = true;
            return axios(cfg);
          }
          goToShellLogin('Please log in again.');
        } catch (refreshErr) {
          // Refresh failed — the session is truly dead.
          goToShellLogin('Please log in again.');
        }
        return Promise.reject(error);
      }
    );

    return () => {
      cancelled = true;
      timers.forEach((t) => { try { clearTimeout(t); clearInterval(t); } catch (_) {} });
      axios.interceptors.request.eject(reqId);
      axios.interceptors.response.eject(respId);
    };
  }, [token]);

  const isAdmin = meRole.is_admin;
  const isReviewer = meRole.is_reviewer;
  const isMember = !meRole.is_admin && !meRole.is_reviewer;

  // -------- Projects --------
  const listProjects = useCallback(async (includeInactive = false) => {
    try {
      const r = await axios.get(`${API}/projects?include_inactive=${includeInactive}`, { headers });
      return r.data;
    } catch (e) {
      console.error('listProjects', e);
      return [];
    }
  }, [token]);

  // Returns ONLY the projects assigned to the caller (used by the Entry tab dropdown).
  const listMyAssignedProjects = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/projects?assigned_only=true`, { headers });
      return r.data;
    } catch (e) {
      console.error('listMyAssignedProjects', e);
      return [];
    }
  }, [token]);

  const createProject = useCallback(async (data) => {
    try {
      const r = await axios.post(`${API}/projects`, data, { headers });
      return { success: true, data: r.data };
    } catch (e) {
      return { success: false, error: e.response?.data?.detail || 'Failed' };
    }
  }, [token]);

  const updateProject = useCallback(async (id, data) => {
    try {
      const r = await axios.put(`${API}/projects/${id}`, data, { headers });
      return { success: true, data: r.data };
    } catch (e) {
      return { success: false, error: e.response?.data?.detail || 'Failed' };
    }
  }, [token]);

  const deleteProject = useCallback(async (id) => {
    try {
      const r = await axios.delete(`${API}/projects/${id}`, { headers });
      return { success: true, data: r.data };
    } catch (e) {
      return { success: false, error: e.response?.data?.detail || 'Failed' };
    }
  }, [token]);

  // -------- Categories --------
  const listCategories = useCallback(async (includeInactive = false) => {
    try {
      const r = await axios.get(`${API}/task-categories?include_inactive=${includeInactive}`, { headers });
      return r.data;
    } catch (e) {
      return [];
    }
  }, [token]);

  const createCategory = useCallback(async (data) => {
    try {
      const r = await axios.post(`${API}/task-categories`, data, { headers });
      return { success: true, data: r.data };
    } catch (e) {
      return { success: false, error: e.response?.data?.detail || 'Failed' };
    }
  }, [token]);

  const updateCategory = useCallback(async (id, data) => {
    try {
      const r = await axios.put(`${API}/task-categories/${id}`, data, { headers });
      return { success: true, data: r.data };
    } catch (e) {
      return { success: false, error: e.response?.data?.detail || 'Failed' };
    }
  }, [token]);

  const deleteCategory = useCallback(async (id) => {
    try {
      const r = await axios.delete(`${API}/task-categories/${id}`, { headers });
      return { success: true, data: r.data };
    } catch (e) {
      return { success: false, error: e.response?.data?.detail || 'Failed' };
    }
  }, [token]);

  // -------- Entries --------
  // Returns the bare `data` array for backward compat with existing callers
  // (TimesheetHistory etc.). For pagination-aware callers, use listEntriesPaged.
  const listEntries = useCallback(async (filters = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') params.append(k, v); });
    try {
      const r = await axios.get(`${API}/entries?${params}`, { headers });
      // Backend now returns {data, total, limit, offset}; older callers want the array only.
      return r.data?.data ?? r.data ?? [];
    } catch (e) {
      return [];
    }
  }, [token]);

  const listEntriesPaged = useCallback(async (filters = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') params.append(k, v); });
    try {
      const r = await axios.get(`${API}/entries?${params}`, { headers });
      return r.data;  // {data, total, limit, offset}
    } catch (e) {
      return { data: [], total: 0, limit: 0, offset: 0 };
    }
  }, [token]);

  // How far back the caller may log on their OWN timesheet. Lets the entry
  // screen grey out days the backend would reject. Returns null on failure so
  // callers fall through to "unrestricted" — the backend still enforces the
  // real rule, so a failed fetch degrades to a server-side error message
  // rather than locking someone out of their own timesheet.
  const loggingWindow = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/entries/logging-window`, { headers });
      return r.data;
    } catch (e) { return null; }
  }, [token]);

  // ---- Backdate requests (ask a team lead to re-open one past day) ----
  const createBackdateRequest = useCallback(async (date, reason) => {
    const r = await axios.post(`${API}/backdate-requests`, { date, reason }, { headers });
    return r.data;
  }, [token]);

  const listBackdateRequests = useCallback(async ({ status, scope = 'self' } = {}) => {
    const params = new URLSearchParams({ scope });
    if (status) params.append('status', status);
    try {
      const r = await axios.get(`${API}/backdate-requests?${params}`, { headers });
      return r.data?.rows || [];
    } catch (e) { return []; }
  }, [token]);

  const reviewBackdateRequest = useCallback(async (requestId, status, note) => {
    const r = await axios.put(
      `${API}/backdate-requests/${requestId}/review`, { status, note: note || '' }, { headers },
    );
    return r.data;
  }, [token]);

  // ---- Weekend summaries → Zoho Cliq (admin) ----
  // The webhook URL is write-only: GET returns a host + configured flag, never
  // the URL, because its auth token sits in the path.
  const getWeekendCliqConfig = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/cliq/weekend`, { headers });
      return r.data;
    } catch (e) { return null; }
  }, [token]);

  const setWeekendCliqConfig = useCallback(async (body) => {
    const r = await axios.put(`${API}/admin/cliq/weekend`, body, { headers });
    return r.data;
  }, [token]);

  const testWeekendCliq = useCallback(async () => {
    const r = await axios.post(`${API}/admin/cliq/weekend/test`, {}, { headers });
    return r.data;
  }, [token]);

  const postWeekendCliqNow = useCallback(async (dateStr) => {
    const r = await axios.post(
      `${API}/admin/cliq/weekend/post-now`, { date: dateStr || null }, { headers },
    );
    return r.data;
  }, [token]);

  const reportWeekendSummaries = useCallback(async (filters = {}) => {
    try {
      const r = await axios.get(`${API}/reports/weekend-summaries?${_qs(filters)}`, { headers });
      return r.data;
    } catch (e) { return null; }
  }, [token]);

  const reportBackdateRequests = useCallback(async (filters = {}) => {
    try {
      const r = await axios.get(`${API}/reports/backdate-requests?${_qs(filters)}`, { headers });
      return r.data;
    } catch (e) { return null; }
  }, [token]);

  const dayDetails = useCallback(async (date, userId) => {
    const params = new URLSearchParams({ date });
    if (userId) params.append('user_id', userId);
    try {
      const r = await axios.get(`${API}/entries/day-details?${params}`, { headers });
      return r.data;
    } catch (e) {
      return { entries: [], submission: null };
    }
  }, [token]);

  // Build the ?user_id=…&reason=… query string for On-Behalf writes. Backend
  // returns 400 if you pass user_id != current_user.id without a reason, so
  // the helper enforces presence here too. Returns '' (no query string) when
  // obo is undefined / empty — the call collapses to today's self-mode.
  const _oboQs = (obo) => {
    if (!obo || !obo.user_id) return '';
    const params = new URLSearchParams();
    params.append('user_id', obo.user_id);
    if (obo.reason) params.append('reason', obo.reason);
    return `?${params.toString()}`;
  };

  const createEntry = useCallback(async (data, obo) => {
    try {
      const r = await axios.post(`${API}/entries${_oboQs(obo)}`, data, { headers });
      return { success: true, data: r.data };
    } catch (e) {
      return { success: false, error: e.response?.data?.detail || 'Failed' };
    }
  }, [token]);

  const createEntriesBulk = useCallback(async (entries, obo) => {
    try {
      const r = await axios.post(`${API}/entries/bulk${_oboQs(obo)}`, { entries }, { headers });
      return { success: true, data: r.data };
    } catch (e) {
      return { success: false, error: e.response?.data?.detail || 'Failed' };
    }
  }, [token]);

  const updateEntry = useCallback(async (id, data, obo) => {
    try {
      // For PUT/DELETE, the entry's owner is implicit from the entry row, so
      // the backend only needs ?reason= for OBO. We pass the full _oboQs for
      // uniformity (user_id is ignored server-side on these routes).
      const r = await axios.put(`${API}/entries/${id}${_oboQs(obo)}`, data, { headers });
      return { success: true, data: r.data };
    } catch (e) {
      return { success: false, error: e.response?.data?.detail || 'Failed' };
    }
  }, [token]);

  const deleteEntry = useCallback(async (id, obo) => {
    try {
      await axios.delete(`${API}/entries/${id}${_oboQs(obo)}`, { headers });
      return { success: true };
    } catch (e) {
      return { success: false, error: e.response?.data?.detail || 'Failed' };
    }
  }, [token]);

  // -------- Weekend Support Summary --------
  // One record per (user, Sat|Sun date). Backs the panel that appears
  // below the Entry rows on weekend days.
  const getWeekendSummary = useCallback(async (date, obo) => {
    try {
      const r = await axios.get(`${API}/weekend-summary/${date}${_oboQs(obo)}`, { headers });
      return { success: true, data: r.data };
    } catch (e) {
      return { success: false, error: e.response?.data?.detail || 'Failed' };
    }
  }, [token]);

  const upsertWeekendSummary = useCallback(async (date, fields, obo) => {
    try {
      const r = await axios.put(`${API}/weekend-summary/${date}${_oboQs(obo)}`, fields, { headers });
      return { success: true, data: r.data };
    } catch (e) {
      return { success: false, error: e.response?.data?.detail || 'Failed' };
    }
  }, [token]);

  // -------- Submissions --------
  const submitDates = useCallback(async (dates, obo) => {
    try {
      const r = await axios.post(`${API}/submissions${_oboQs(obo)}`, { dates }, { headers });
      return { success: true, data: r.data };
    } catch (e) {
      return { success: false, error: e.response?.data?.detail || 'Failed' };
    }
  }, [token]);

  const listSubmissions = useCallback(async (filters = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') params.append(k, v); });
    try {
      const r = await axios.get(`${API}/submissions?${params}`, { headers });
      return r.data;
    } catch (e) {
      return [];
    }
  }, [token]);

  const reviewSubmission = useCallback(async (id, status, note = '') => {
    try {
      const r = await axios.put(`${API}/submissions/${id}/review`, { status, note }, { headers });
      return { success: true, data: r.data };
    } catch (e) {
      return { success: false, error: e.response?.data?.detail || 'Failed' };
    }
  }, [token]);

  // -------- Assignments --------
  const getAssignments = useCallback(async (userId) => {
    try {
      const r = await axios.get(`${API}/user-assignments/${userId}`, { headers });
      return r.data;
    } catch (e) {
      return null;
    }
  }, [token]);

  const setAssignments = useCallback(async (userId, data) => {
    try {
      const r = await axios.put(`${API}/user-assignments/${userId}`, data, { headers });
      return { success: true, data: r.data };
    } catch (e) {
      return { success: false, error: e.response?.data?.detail || 'Failed' };
    }
  }, [token]);

  const teamMembers = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/team-members`, { headers });
      return r.data;
    } catch (e) {
      return [];
    }
  }, [token]);

  // -------- Lookups --------
  const usersLookup = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/users-lookup`, { headers });
      return r.data;
    } catch (e) {
      return [];
    }
  }, [token]);

  const employeesLookup = useCallback(async (search = '') => {
    try {
      const r = await axios.get(`${API}/employees-lookup?search=${encodeURIComponent(search)}`, { headers });
      return r.data;
    } catch (e) {
      return [];
    }
  }, [token]);

  // -------- Notifications --------
  const listNotifications = useCallback(async (onlyUnread = false) => {
    try {
      const r = await axios.get(`${API}/notifications?only_unread=${onlyUnread}`, { headers });
      return r.data;
    } catch (e) {
      return [];
    }
  }, [token]);

  const unreadCount = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/notifications/unread-count`, { headers });
      return r.data?.count || 0;
    } catch (e) {
      return 0;
    }
  }, [token]);

  const markRead = useCallback(async (id) => {
    try {
      await axios.put(`${API}/notifications/${id}/read`, {}, { headers });
      return true;
    } catch (e) {
      return false;
    }
  }, [token]);

  const markAllRead = useCallback(async () => {
    try {
      await axios.put(`${API}/notifications/read-all`, {}, { headers });
      return true;
    } catch (e) {
      return false;
    }
  }, [token]);

  // -------- Reports --------
  const reportPersonal = useCallback(async (filters = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
    try {
      const r = await axios.get(`${API}/reports/personal?${params}`, { headers });
      return r.data;
    } catch (e) {
      return null;
    }
  }, [token]);

  const reportPersonalDetailed = useCallback(async (filters = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
    try {
      const r = await axios.get(`${API}/reports/personal-detailed?${params}`, { headers });
      return r.data;
    } catch (e) {
      return null;
    }
  }, [token]);

  const reportProject = useCallback(async (filters = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
    try {
      const r = await axios.get(`${API}/reports/project?${params}`, { headers });
      return r.data;
    } catch (e) {
      return null;
    }
  }, [token]);

  const reportProjectDetailed = useCallback(async (filters = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
    try {
      const r = await axios.get(`${API}/reports/project-detailed?${params}`, { headers });
      return r.data;
    } catch (e) {
      return null;
    }
  }, [token]);

  const exportReportUrl = (format, filters = {}) => {
    const params = new URLSearchParams({ format });
    Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
    return `${API}/reports/export?${params}&token=${token}`;
  };

  const downloadReport = useCallback(async (format, filters = {}) => {
    const params = new URLSearchParams({ format });
    Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
    try {
      const r = await axios.get(`${API}/reports/export?${params}`, { headers, responseType: 'blob' });
      const blob = new Blob([r.data], { type: r.headers['content-type'] });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = format === 'csv' ? 'timesheet_report.csv' : 'timesheet_report.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      return { success: true };
    } catch (e) {
      return { success: false, error: e.response?.data?.detail || 'Failed' };
    }
  }, [token]);

  // -------- Change Requests --------
  const createChangeRequest = useCallback(async ({ date, reason }) => {
    try {
      const r = await axios.post(`${API}/change-requests`, { date, reason }, { headers });
      return { success: true, data: r.data };
    } catch (e) {
      return { success: false, error: e.response?.data?.detail || 'Failed' };
    }
  }, [token]);

  const listChangeRequests = useCallback(async ({ status, scope = 'self' } = {}) => {
    const params = new URLSearchParams({ scope });
    if (status) params.append('status', status);
    try {
      const r = await axios.get(`${API}/change-requests?${params}`, { headers });
      return r.data || [];
    } catch (e) { return []; }
  }, [token]);

  const reviewChangeRequest = useCallback(async (id, status, note = '') => {
    try {
      const r = await axios.put(`${API}/change-requests/${id}/review`, { status, note }, { headers });
      return { success: true, data: r.data };
    } catch (e) {
      return { success: false, error: e.response?.data?.detail || 'Failed' };
    }
  }, [token]);

  const submissionAudit = useCallback(async ({ submission_id, user_id, date } = {}) => {
    const params = new URLSearchParams();
    if (submission_id) params.append('submission_id', submission_id);
    if (user_id) params.append('user_id', user_id);
    if (date) params.append('date', date);
    try {
      const r = await axios.get(`${API}/submission-audit?${params}`, { headers });
      return r.data || [];
    } catch (e) { return []; }
  }, [token]);

  // -------- Excel-parity Reports --------
  const _qs = (filters) => {
    const params = new URLSearchParams();
    Object.entries(filters || {}).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') params.append(k, v); });
    return params.toString();
  };

  const reportSummary = useCallback(async (filters = {}) => {
    try {
      const r = await axios.get(`${API}/reports/summary?${_qs(filters)}`, { headers });
      return r.data;
    } catch (e) { return null; }
  }, [token]);

  const reportDailyMatrix = useCallback(async (filters = {}) => {
    try {
      const r = await axios.get(`${API}/reports/daily-matrix?${_qs(filters)}`, { headers });
      return r.data;
    } catch (e) { return null; }
  }, [token]);

  const reportAnomalies = useCallback(async (filters = {}) => {
    try {
      const r = await axios.get(`${API}/reports/anomalies?${_qs(filters)}`, { headers });
      return r.data;
    } catch (e) { return null; }
  }, [token]);

  const reportMissing = useCallback(async (filters = {}) => {
    try {
      const r = await axios.get(`${API}/reports/missing?${_qs(filters)}`, { headers });
      return r.data;
    } catch (e) { return null; }
  }, [token]);

  // Per-resource logging-timeliness stats (Phase 2 of late-entry work).
  // Returns { rows: [...], team_on_time_pct, team_total_entries }. Rows
  // are pre-sorted worst-first by the backend.
  const reportTimeliness = useCallback(async (filters = {}) => {
    try {
      const r = await axios.get(`${API}/reports/timeliness?${_qs(filters)}`, { headers });
      return r.data;
    } catch (e) { return null; }
  }, [token]);

  // Self-scoped timeliness for the dashboard widget. Unlike reportTimeliness
  // this is open to every role — the backend only ever returns the caller's
  // own numbers plus an org-wide average, so there's nothing to gate.
  const reportTimelinessMe = useCallback(async (filters = {}) => {
    try {
      const r = await axios.get(`${API}/reports/timeliness/me?${_qs(filters)}`, { headers });
      return r.data;
    } catch (e) { return null; }
  }, [token]);

  const reportPmyEntries = useCallback(async (filters = {}) => {
    try {
      const r = await axios.get(`${API}/reports/pmy-entries?${_qs(filters)}`, { headers });
      return r.data;
    } catch (e) { return null; }
  }, [token]);

  // -------- Backup (ts_admin) --------
  const backupStatus = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/backup/status`, { headers });
      return r.data;
    } catch (e) { return null; }
  }, [token]);

  const downloadBackup = useCallback(async () => {
    try {
      const r = await axios.post(`${API}/admin/backup/download`, {}, { headers, responseType: 'blob' });
      const blob = new Blob([r.data], { type: r.headers['content-type'] || 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const cd = r.headers['content-disposition'] || '';
      const m = cd.match(/filename="([^"]+)"/);
      a.download = m ? m[1] : `harmony_timesheet_backup_${new Date().toISOString().slice(0,10).replace(/-/g, '')}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      return { success: true };
    } catch (e) {
      return { success: false, error: e.response?.data?.detail || 'Failed' };
    }
  }, [token]);

  // Inspect (dry-run) an uploaded timesheet backup JSON. Returns the
  // server's per-collection summary so the UI can show "backup has N,
  // dev has M" before the admin commits to a strategy.
  const backupRestoreInspect = useCallback(async (file) => {
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(`${API}/admin/backup/restore/inspect`, fd, { headers });
      return { success: true, data: r.data };
    } catch (e) {
      return { success: false, error: e.response?.data?.detail || e.message || 'Failed' };
    }
  }, [token]);

  // Execute the restore with the chosen strategy. Returns the server's
  // per-collection report (inserted/updated/skipped/dropped) so the UI
  // can show what actually happened.
  const backupRestoreExecute = useCallback(async (file, strategy) => {
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('strategy', strategy);
      const r = await axios.post(`${API}/admin/backup/restore/execute`, fd, { headers });
      return { success: true, data: r.data };
    } catch (e) {
      return { success: false, error: e.response?.data?.detail || e.message || 'Failed' };
    }
  }, [token]);

  // -------- Holidays --------
  const listHolidays = useCallback(async (year) => {
    try {
      const url = year ? `${API}/admin/holidays?year=${year}` : `${API}/admin/holidays`;
      const r = await axios.get(url, { headers });
      return r.data || [];
    } catch (e) { return []; }
  }, [token]);

  const createHoliday = useCallback(async (data) => {
    try {
      const r = await axios.post(`${API}/admin/holidays`, data, { headers });
      return { success: true, data: r.data };
    } catch (e) { return { success: false, error: e.response?.data?.detail || 'Failed' }; }
  }, [token]);

  const updateHoliday = useCallback(async (id, data) => {
    try {
      const r = await axios.put(`${API}/admin/holidays/${id}`, data, { headers });
      return { success: true, data: r.data };
    } catch (e) { return { success: false, error: e.response?.data?.detail || 'Failed' }; }
  }, [token]);

  const deleteHoliday = useCallback(async (id) => {
    try {
      await axios.delete(`${API}/admin/holidays/${id}`, { headers });
      return { success: true };
    } catch (e) { return { success: false, error: e.response?.data?.detail || 'Failed' }; }
  }, [token]);

  const getLeaveProject = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/holidays/leave-project`, { headers });
      return r.data;
    } catch (e) { return null; }
  }, [token]);

  const setLeaveProject = useCallback(async (project_id) => {
    try {
      const r = await axios.put(`${API}/admin/holidays/leave-project`, { project_id }, { headers });
      return { success: true, data: r.data };
    } catch (e) { return { success: false, error: e.response?.data?.detail || 'Failed' }; }
  }, [token]);

  const prefillHolidayNow = useCallback(async (id) => {
    try {
      const r = await axios.post(`${API}/admin/holidays/${id}/prefill`, {}, { headers });
      return { success: true, data: r.data };
    } catch (e) { return { success: false, error: e.response?.data?.detail || 'Failed' }; }
  }, [token]);

  const prefillHolidayForSelf = useCallback(async (dateStr) => {
    try {
      const r = await axios.post(`${API}/entries/prefill-holiday?date=${dateStr}`, {}, { headers });
      return { success: true, data: r.data };
    } catch (e) { return { success: false, error: e.response?.data?.detail || 'Failed' }; }
  }, [token]);

  // -------- Email settings (ts_admin) --------
  const emailStatus = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/email/status`, { headers });
      return r.data;
    } catch (e) { return null; }
  }, [token]);

  const triggerEmailJob = useCallback(async (job, force = false) => {
    try {
      const r = await axios.post(`${API}/admin/email/trigger`, { job, force }, { headers });
      return { success: true, data: r.data };
    } catch (e) {
      return { success: false, error: e.response?.data?.detail || 'Failed' };
    }
  }, [token]);

  const sendTestEmail = useCallback(async (recipient) => {
    try {
      const r = await axios.post(`${API}/admin/email/test?recipient=${encodeURIComponent(recipient)}`, {}, { headers });
      return { success: true, data: r.data };
    } catch (e) {
      return { success: false, error: e.response?.data?.detail || 'Failed' };
    }
  }, [token]);

  // Toggle a single event (morning_summary | weekly_digest) without touching
  // the master switches.
  const setEmailEventEnabled = useCallback(async (event, enabled) => {
    try {
      const r = await axios.post(`${API}/admin/email/event-toggle`, { event, enabled }, { headers });
      return { success: true, data: r.data };
    } catch (e) {
      return { success: false, error: e.response?.data?.detail || 'Failed' };
    }
  }, [token]);

  // Per-user email opt-out — for HR / management / contractors we don't want
  // to bother. Returns enriched rows (display_name, email, reason).
  const listEmailOptOuts = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/email/opt-out`, { headers });
      return r.data || [];
    } catch (e) { return []; }
  }, [token]);

  const addEmailOptOut = useCallback(async (user_id, reason) => {
    try {
      const r = await axios.post(`${API}/admin/email/opt-out`, { user_id, reason }, { headers });
      return { success: true, data: r.data };
    } catch (e) {
      return { success: false, error: e.response?.data?.detail || 'Failed' };
    }
  }, [token]);

  const removeEmailOptOut = useCallback(async (user_id) => {
    try {
      const r = await axios.delete(`${API}/admin/email/opt-out/${user_id}`, { headers });
      return { success: true, data: r.data };
    } catch (e) {
      return { success: false, error: e.response?.data?.detail || 'Failed' };
    }
  }, [token]);

  // -------- User Roles (admin) --------
  const listUserRoles = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/user-roles`, { headers });
      return r.data || [];
    } catch (e) { return []; }
  }, [token]);

  const setUserRole = useCallback(async (userId, newRole) => {
    try {
      const r = await axios.put(`${API}/user-roles/${userId}`, { role: newRole }, { headers });
      return { success: true, data: r.data };
    } catch (e) {
      return { success: false, error: e.response?.data?.detail || 'Failed' };
    }
  }, [token]);

  // Report exclusions — admin marks non-time-logging users (HR / management /
  // alumni) so they don't clutter reports or receive the daily discipline email.
  const listReportExclusions = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/report-exclusions`, { headers });
      return r.data || [];
    } catch (e) { return []; }
  }, [token]);

  const setReportExclusion = useCallback(async (userId, excluded) => {
    try {
      const r = await axios.put(`${API}/admin/report-exclusions/${userId}`, { excluded }, { headers });
      return { success: true, data: r.data };
    } catch (e) {
      return { success: false, error: e.response?.data?.detail || 'Failed' };
    }
  }, [token]);

  // -------- Dashboard --------
  // `weekStart` is optional. When given (YYYY-MM-DD), returns that week; otherwise today's week.
  const myWeek = useCallback(async (weekStart) => {
    try {
      const url = weekStart
        ? `${API}/dashboard/my-week?week_start=${weekStart}`
        : `${API}/dashboard/my-week`;
      const r = await axios.get(url, { headers });
      return r.data;
    } catch (e) {
      return null;
    }
  }, [token]);

  // Past-weekday gaps for the caller — last week + current week, weekends + today + future excluded.
  const myPending = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/dashboard/my-pending`, { headers });
      return r.data;
    } catch (e) {
      return null;
    }
  }, [token]);

  // -------- Per-user preferences (default project / category + hidden categories) --------
  // Never throws — getUserPreferences returns the backend's defaults shape if
  // the network call fails, so the Entry tab and Dashboard card don't have
  // to guard their render against a null pref object.
  const getUserPreferences = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/user/preferences`, { headers });
      return r.data;
    } catch (e) {
      return { default_project_id: null, default_category_id: null, hidden_category_ids: [] };
    }
  }, [token]);

  const updateUserPreferences = useCallback(async (prefs) => {
    try {
      const r = await axios.put(`${API}/user/preferences`, prefs, { headers });
      return { success: true, data: r.data };
    } catch (e) {
      return { success: false, error: e.response?.data?.detail || 'Failed' };
    }
  }, [token]);

  return (
    <TimesheetContext.Provider value={{
      loading, setLoading, isAdmin, isSuperAdmin, isReviewer, isMember, meRole, role, userName, token,
      listUserRoles, setUserRole, listReportExclusions, setReportExclusion,
      emailStatus, triggerEmailJob, sendTestEmail,
      setEmailEventEnabled, listEmailOptOuts, addEmailOptOut, removeEmailOptOut,
      listHolidays, createHoliday, updateHoliday, deleteHoliday,
      getLeaveProject, setLeaveProject, prefillHolidayNow, prefillHolidayForSelf,
      backupStatus, downloadBackup, backupRestoreInspect, backupRestoreExecute,
      // projects
      listProjects, listMyAssignedProjects, createProject, updateProject, deleteProject,
      // categories
      listCategories, createCategory, updateCategory, deleteCategory,
      // entries
      listEntries, listEntriesPaged, dayDetails, loggingWindow, createEntry, createEntriesBulk, updateEntry, deleteEntry,
      createBackdateRequest, listBackdateRequests, reviewBackdateRequest, reportBackdateRequests,
      reportWeekendSummaries,
      getWeekendCliqConfig, setWeekendCliqConfig, testWeekendCliq, postWeekendCliqNow,
      // weekend summary
      getWeekendSummary, upsertWeekendSummary,
      // submissions
      submitDates, listSubmissions, reviewSubmission,
      // change requests + audit
      createChangeRequest, listChangeRequests, reviewChangeRequest, submissionAudit,
      // assignments
      getAssignments, setAssignments, teamMembers,
      // lookups
      usersLookup, employeesLookup,
      // notifications
      listNotifications, unreadCount, markRead, markAllRead,
      // reports
      reportPersonal, reportPersonalDetailed, reportProject, reportProjectDetailed,
      reportSummary, reportDailyMatrix, reportAnomalies, reportMissing, reportPmyEntries, reportTimeliness,
      reportTimelinessMe,
      exportReportUrl, downloadReport,
      // dashboard
      myWeek, myPending,
      // member preferences
      getUserPreferences, updateUserPreferences,
    }}>
      {children}
    </TimesheetContext.Provider>
  );
};

export default TimesheetContext;
